import { decryptAesGcm, encryptAesGcm } from '../../lib/pqc/aesgcm';
import { bytesToBase64Url, bytesToUtf8, utf8ToBytes } from '../../lib/pqc/codecs';
import { getPqcEngine, type MlKemVariant } from '../../lib/pqc/engine';
import { hkdfSha256 } from '../../lib/pqc/hkdf';
import {
  MAX_MESSAGE_BYTES,
  copyToClipboard,
  downloadText,
  formatBytes,
  formatDuration,
  getMessageBytes,
  setError,
  setStatus,
  updateByteCounter,
} from './pqc-shared';

const variantSelect = document.querySelector<HTMLSelectElement>('#mlkem-variant');
const generateButton = document.querySelector<HTMLButtonElement>('#mlkem-generate');
const pkField = document.querySelector<HTMLTextAreaElement>('#mlkem-pk');
const pkSize = document.querySelector<HTMLSpanElement>('#mlkem-pk-size');
const pkCopy = document.querySelector<HTMLButtonElement>('#mlkem-pk-copy');
const pkExport = document.querySelector<HTMLButtonElement>('#mlkem-pk-export');
const skField = document.querySelector<HTMLTextAreaElement>('#mlkem-sk');
const skSize = document.querySelector<HTMLSpanElement>('#mlkem-sk-size');
const sizePkSummary = document.querySelector<HTMLSpanElement>('#mlkem-size-pk');
const sizeSkSummary = document.querySelector<HTMLSpanElement>('#mlkem-size-sk');
const sizeCtSummary = document.querySelector<HTMLSpanElement>('#mlkem-size-ct');
const sizeSharedSummary = document.querySelector<HTMLSpanElement>('#mlkem-size-shared');
const sizeAesSummary = document.querySelector<HTMLSpanElement>('#mlkem-size-aes-ct');
const skCopy = document.querySelector<HTMLButtonElement>('#mlkem-sk-copy');
const skToggle = document.querySelector<HTMLButtonElement>('#mlkem-sk-toggle');
const exportAll = document.querySelector<HTMLButtonElement>('#mlkem-export-all');
const encapsulateButton = document.querySelector<HTMLButtonElement>('#mlkem-encapsulate');
const ctField = document.querySelector<HTMLTextAreaElement>('#mlkem-ct');
const ctSize = document.querySelector<HTMLSpanElement>('#mlkem-ct-size');
const sharedField = document.querySelector<HTMLTextAreaElement>('#mlkem-shared');
const sharedToggle = document.querySelector<HTMLButtonElement>('#mlkem-shared-toggle');
const messageInput = document.querySelector<HTMLTextAreaElement>('#mlkem-message');
const messageSize = document.querySelector<HTMLSpanElement>('#mlkem-message-size');
const encryptButton = document.querySelector<HTMLButtonElement>('#mlkem-encrypt');
const aesCipherField = document.querySelector<HTMLTextAreaElement>('#mlkem-aes-ct');
const aesIvField = document.querySelector<HTMLTextAreaElement>('#mlkem-aes-iv');
const aesSaltField = document.querySelector<HTMLTextAreaElement>('#mlkem-aes-salt');
const decryptButton = document.querySelector<HTMLButtonElement>('#mlkem-decrypt');
const resetButton = document.querySelector<HTMLButtonElement>('#mlkem-reset');
const decryptedField = document.querySelector<HTMLTextAreaElement>('#mlkem-decrypted');
const keygenTime = document.querySelector<HTMLSpanElement>('#mlkem-time-keygen');
const encapsTime = document.querySelector<HTMLSpanElement>('#mlkem-time-encaps');
const decapsTime = document.querySelector<HTMLSpanElement>('#mlkem-time-decaps');
const aesEncryptTime = document.querySelector<HTMLSpanElement>('#mlkem-time-aes-encrypt');
const aesDecryptTime = document.querySelector<HTMLSpanElement>('#mlkem-time-aes-decrypt');
const statusEl = document.querySelector<HTMLParagraphElement>('#mlkem-status');
const errorEl = document.querySelector<HTMLParagraphElement>('#mlkem-error');

if (
  !variantSelect ||
  !generateButton ||
  !pkField ||
  !pkSize ||
  !pkCopy ||
  !pkExport ||
  !skField ||
  !skSize ||
  !sizePkSummary ||
  !sizeSkSummary ||
  !sizeCtSummary ||
  !sizeSharedSummary ||
  !sizeAesSummary ||
  !skCopy ||
  !skToggle ||
  !exportAll ||
  !encapsulateButton ||
  !ctField ||
  !ctSize ||
  !sharedField ||
  !sharedToggle ||
  !messageInput ||
  !messageSize ||
  !encryptButton ||
  !aesCipherField ||
  !aesIvField ||
  !aesSaltField ||
  !decryptButton ||
  !resetButton ||
  !decryptedField ||
  !keygenTime ||
  !encapsTime ||
  !decapsTime ||
  !aesEncryptTime ||
  !aesDecryptTime ||
  !statusEl ||
  !errorEl
) {
  throw new Error('ML-KEM demo elements not found.');
}

const enginePromise = getPqcEngine();
const hkdfInfo = utf8ToBytes('pqc-ml-kem-demo');

let publicKey: Uint8Array | null = null;
let secretKey: Uint8Array | null = null;
let kemCipherText: Uint8Array | null = null;
let kemSharedSecret: Uint8Array | null = null;
let hkdfSalt: Uint8Array | null = null;
let aesIv: Uint8Array | null = null;
let aesCiphertext: Uint8Array | null = null;

function zeroize(bytes: Uint8Array | null) {
  if (bytes) {
    bytes.fill(0);
  }
}

function resetDerivedState() {
  zeroize(kemSharedSecret);
  kemCipherText = null;
  kemSharedSecret = null;
  hkdfSalt = null;
  aesIv = null;
  aesCiphertext = null;
  ctField.value = '';
  ctSize.textContent = '-';
  sizeCtSummary.textContent = '-';
  sizeSharedSummary.textContent = '-';
  sizeAesSummary.textContent = '-';
  sharedField.value = '';
  sharedField.classList.add('hidden');
  sharedToggle.textContent = 'Reveal shared secret';
  aesCipherField.value = '';
  aesIvField.value = '';
  aesSaltField.value = '';
  decryptedField.value = '';
  aesEncryptTime.textContent = '-';
  aesDecryptTime.textContent = '-';
  decapsTime.textContent = '-';
  encapsTime.textContent = '-';
}

function setBusy(isBusy: boolean) {
  generateButton.disabled = isBusy;
  encapsulateButton.disabled = isBusy;
  encryptButton.disabled = isBusy;
  decryptButton.disabled = isBusy;
}

function updateKeyFields() {
  if (publicKey) {
    pkField.value = bytesToBase64Url(publicKey);
    pkSize.textContent = formatBytes(publicKey.length);
    sizePkSummary.textContent = formatBytes(publicKey.length);
  } else {
    pkField.value = '';
    pkSize.textContent = '-';
    sizePkSummary.textContent = '-';
  }
  if (secretKey) {
    skField.value = bytesToBase64Url(secretKey);
    skSize.textContent = formatBytes(secretKey.length);
    sizeSkSummary.textContent = formatBytes(secretKey.length);
  } else {
    skField.value = '';
    skSize.textContent = '-';
    sizeSkSummary.textContent = '-';
  }
  skField.classList.add('hidden');
  skToggle.textContent = 'Reveal secret key';
}

function updateSharedField() {
  if (kemSharedSecret) {
    sharedField.value = bytesToBase64Url(kemSharedSecret);
    sizeSharedSummary.textContent = formatBytes(kemSharedSecret.length);
  } else {
    sharedField.value = '';
    sizeSharedSummary.textContent = '-';
  }
  sharedField.classList.add('hidden');
  sharedToggle.textContent = 'Reveal shared secret';
}

messageInput.addEventListener('input', () => {
  updateByteCounter(messageInput, messageSize, MAX_MESSAGE_BYTES);
});
updateByteCounter(messageInput, messageSize, MAX_MESSAGE_BYTES);

skToggle.addEventListener('click', () => {
  const isHidden = skField.classList.toggle('hidden');
  skToggle.textContent = isHidden ? 'Reveal secret key' : 'Hide secret key';
});

sharedToggle.addEventListener('click', () => {
  const isHidden = sharedField.classList.toggle('hidden');
  sharedToggle.textContent = isHidden ? 'Reveal shared secret' : 'Hide shared secret';
});

pkCopy.addEventListener('click', async () => {
  await copyToClipboard(pkField.value);
  setStatus(statusEl, 'Public key copied.');
});

skCopy.addEventListener('click', async () => {
  await copyToClipboard(skField.value);
  setStatus(statusEl, 'Secret key copied.');
});

pkExport.addEventListener('click', () => {
  if (!publicKey) return;
  downloadText('ml-kem-public-key.txt', pkField.value);
});

exportAll.addEventListener('click', () => {
  if (!publicKey || !secretKey) return;
  const payload = `Public key:\n${pkField.value}\n\nSecret key:\n${skField.value}`;
  downloadText('ml-kem-keys-unsafe.txt', payload);
});

generateButton.addEventListener('click', async () => {
  setError(errorEl, '');
  setStatus(statusEl, 'Generating keypair...');
  setBusy(true);
  const variant = variantSelect.value as MlKemVariant;
  try {
    const start = performance.now();
    const engine = await enginePromise;
    const result = await engine.mlKemKeygen(variant);
    keygenTime.textContent = formatDuration(performance.now() - start);
    zeroize(publicKey);
    zeroize(secretKey);
    publicKey = result.publicKey;
    secretKey = result.secretKey;
    updateKeyFields();
    resetDerivedState();
    updateSharedField();
    setStatus(statusEl, 'Recipient keypair ready.');
  } catch (error) {
    setError(errorEl, 'Unable to generate ML-KEM keypair.');
    setStatus(statusEl, '');
  } finally {
    setBusy(false);
  }
});

encapsulateButton.addEventListener('click', async () => {
  setError(errorEl, '');
  setStatus(statusEl, '');
  if (!publicKey) {
    setError(errorEl, 'Generate a recipient keypair first.');
    return;
  }
  setStatus(statusEl, 'Encapsulating to recipient...');
  setBusy(true);
  const variant = variantSelect.value as MlKemVariant;
  try {
    const start = performance.now();
    const engine = await enginePromise;
    const result = await engine.mlKemEncapsulate(variant, publicKey);
    encapsTime.textContent = formatDuration(performance.now() - start);
    zeroize(kemSharedSecret);
    resetDerivedState();
    kemCipherText = result.cipherText;
    kemSharedSecret = result.sharedSecret;
    ctField.value = bytesToBase64Url(kemCipherText);
    ctSize.textContent = formatBytes(kemCipherText.length);
    sizeCtSummary.textContent = formatBytes(kemCipherText.length);
    updateSharedField();
    setStatus(statusEl, 'Ciphertext created on sender side.');
  } catch (error) {
    setError(errorEl, 'Encapsulation failed.');
    setStatus(statusEl, '');
  } finally {
    setBusy(false);
  }
});

encryptButton.addEventListener('click', async () => {
  setError(errorEl, '');
  setStatus(statusEl, '');
  if (!kemSharedSecret) {
    setError(errorEl, 'Encapsulate to produce a shared secret first.');
    return;
  }
  if (!crypto?.subtle) {
    setError(errorEl, 'WebCrypto is not available in this browser.');
    return;
  }
  const messageBytes = getMessageBytes(messageInput.value);
  if (messageBytes.length > MAX_MESSAGE_BYTES) {
    setError(errorEl, 'Message is too large for the demo.');
    return;
  }
  setStatus(statusEl, 'Deriving AES key and encrypting...');
  setBusy(true);
  try {
    hkdfSalt = crypto.getRandomValues(new Uint8Array(16));
    aesIv = crypto.getRandomValues(new Uint8Array(12));
    const aesKey = await hkdfSha256({
      secret: kemSharedSecret,
      salt: hkdfSalt,
      info: hkdfInfo,
      length: 32,
    });
    const start = performance.now();
    aesCiphertext = await encryptAesGcm({
      key: aesKey,
      iv: aesIv,
      plaintext: messageBytes,
    });
    aesEncryptTime.textContent = formatDuration(performance.now() - start);
    aesKey.fill(0);
    aesCipherField.value = bytesToBase64Url(aesCiphertext);
    aesIvField.value = bytesToBase64Url(aesIv);
    aesSaltField.value = bytesToBase64Url(hkdfSalt);
    sizeAesSummary.textContent = formatBytes(aesCiphertext.length);
    decryptedField.value = '';
    setStatus(statusEl, 'Message encrypted with AES-GCM.');
  } catch (error) {
    console.error('ML-KEM AES encryption failed', error);
    setError(errorEl, 'Encryption failed. This browser may not support HKDF or AES-GCM.');
    setStatus(statusEl, '');
  } finally {
    setBusy(false);
  }
});

decryptButton.addEventListener('click', async () => {
  setError(errorEl, '');
  setStatus(statusEl, '');
  if (!secretKey || !kemCipherText || !hkdfSalt || !aesIv || !aesCiphertext) {
    setError(errorEl, 'Generate keys, encapsulate, and encrypt before decrypting.');
    return;
  }
  setStatus(statusEl, 'Decapsulating and decrypting...');
  setBusy(true);
  const variant = variantSelect.value as MlKemVariant;
  try {
    const decapsStart = performance.now();
    const engine = await enginePromise;
    const result = await engine.mlKemDecapsulate(variant, secretKey, kemCipherText);
    decapsTime.textContent = formatDuration(performance.now() - decapsStart);
    const aesKey = await hkdfSha256({
      secret: result.sharedSecret,
      salt: hkdfSalt,
      info: hkdfInfo,
      length: 32,
    });
    const decryptStart = performance.now();
    const plaintext = await decryptAesGcm({
      key: aesKey,
      iv: aesIv,
      ciphertext: aesCiphertext,
    });
    aesDecryptTime.textContent = formatDuration(performance.now() - decryptStart);
    aesKey.fill(0);
    result.sharedSecret.fill(0);
    decryptedField.value = bytesToUtf8(plaintext);
    setStatus(statusEl, 'Decryption successful.');
  } catch (error) {
    setError(errorEl, 'Unable to decrypt with ML-KEM-derived key.');
    setStatus(statusEl, '');
  } finally {
    setBusy(false);
  }
});

resetButton.addEventListener('click', () => {
  zeroize(publicKey);
  zeroize(secretKey);
  zeroize(kemSharedSecret);
  publicKey = null;
  secretKey = null;
  resetDerivedState();
  updateKeyFields();
  messageInput.value = '';
  updateByteCounter(messageInput, messageSize, MAX_MESSAGE_BYTES);
  keygenTime.textContent = '-';
  setStatus(statusEl, 'Reset complete.');
  setError(errorEl, '');
});
